import ApiService from '@/common/api.service'
import * as utils from '@/common/utils'
import * as dt from '@/common/dateUtils'
import { selectedUsers } from '@/store/administration/users/findUsersWidget.helpers'

/**
 * Returns all properties and their default state.
 *
 * @return Object
 */
function properties () {
  return {
    date_range_type: 'day', // day, month, date_range
    date_range_day: dt.dateShort(dt.now()),
    date_range_month: dt.monthShort(dt.now()),
    date_range_from: dt.dateShort(dt.now().startOf('month')),
    date_range_to: dt.dateShort(dt.now()),
    provider: 'gdspo',
    search_mode: 'all-users',
    summary_data: {
      users: {},
      grand_total: 0
    }
  }
}

function transformParams (data) {
  return {
    date_range_type: data.date_range_type, // day, month, range
    date_range_day: data.date_range_day,
    date_range_month: data.date_range_month,
    date_range_from: data.date_range_from,
    date_range_to: data.date_range_to,
    user_ids: data.search_mode !== 'all-users' ? data.user_ids : 'all_users',
    provider: data.provider,
    summary: {}
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties()
  }
}

function formDefaults () {
  return {
    providers: []
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  original: properties(),
  isModified: false,
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  }

}

export const actions = {
  /**
   * Request form data from the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchFormData (context) {
    return ApiService
      .get(`unit/commands/providers`)
      .then((response) => {
        context.commit('setFormData', response.data.data)
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  findSummary (context, params) {
    let data = context.getters['get']
    data.user_ids = Object.keys(selectedUsers('users')).map(key => {
      return Number(key)
    })
    return ApiService
      .get(`unit/commands/send/logs/summary`, transformParams(data))
      .then((response) => {
        context.commit('setSummaryData', response.data.data)
        return response
      })
  }
}

export const mutations = {
  setSummaryData (state, data) {
    state.data.summary_data = Object.assign({}, data)
  },

  setFormData (state, data) {
    state.form = {
      providers: data
    }
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * removes an element from an array of a specific property
   * and replaced it with a new element.
   *
   * @param state   Object
   * @param name    String Name of property
   * @param index   Number The index of the element to remove
   * @param replacement New element to replace the old
   */
  spliceProp (state, { name, index, replacement }) {
    state.isModified = true
    state.data[name].splice(index, 1, replacement)
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.original = properties()
    state.isModified = false
    state.isLoading = false
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = formDefaults()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/units/commands/commandSummaryModule.js